import React from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Grid } from '@mui/material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import { renderTextField, renderDropdown } from 'components/form/field/redux-field';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { SignatureRequirementClient } from 'clients/signature-requirement-client';
import {
  TriggerType,
  ICreateSignatureRequirementRequest,
} from 'models/signature-requirement/types';
import { TRIGGER_TYPE_OPTIONS } from '../constants';
import { AddSignatureRequirementModalStyles } from './add-signature-requirement-modal.styles';

interface IFormValues {
  name: string;
  site: { label: string; value: number };
  trigger: string;
  insurance_bin: string;
  insurance_pcn: string;
  insurance_group: string;
}

interface IAddModalProps {
  open: boolean;
  selectedCustomerId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

type IAddSignatureRequirementModalProps = IAddModalProps &
  InjectedFormProps<IFormValues, IAddModalProps> &
  WithStyles<typeof AddSignatureRequirementModalStyles>;

const required = (value: any) => (value ? undefined : 'Required');

const AddSignatureRequirementModalComponent: React.FC<
  IAddSignatureRequirementModalProps
> = props => {
  const { classes, handleSubmit, reset } = props;
  const [loading, setLoading] = React.useState<boolean>(false);

  const customers = useTypedSelector(state => state.filters.customers);
  const selectedCustomer = customers.find(c => c.id === props.selectedCustomerId);

  const formValues = useTypedSelector(
    state => state.form?.AddSignatureRequirementModal?.values as IFormValues,
  );

  const showInsuranceFields = formValues?.trigger === TriggerType.Insurance;

  const handleClose = () => {
    reset();
    props.onCancel();
  };

  const onSubmit = async (values: IFormValues) => {
    if (!values.trigger) return;

    setLoading(true);
    try {
      const payload: ICreateSignatureRequirementRequest = {
        name: values.name,
        customer_id: props.selectedCustomerId,
        trigger_type: values.trigger as TriggerType,
        ...(showInsuranceFields && {
          insurance_bin: values.insurance_bin,
          insurance_pcn: values.insurance_pcn || undefined,
          insurance_group: values.insurance_group || undefined,
        }),
      };

      await SignatureRequirementClient.create(payload);
      props.onSuccess();
      handleClose();
    } catch (error) {
      // TODO: Add notifyError
    } finally {
      setLoading(false);
    }
  };

  const triggerOptions = TRIGGER_TYPE_OPTIONS.filter(opt => opt.value !== 'all');
  const siteOptions = selectedCustomer
    ? [{ label: selectedCustomer.name, value: selectedCustomer.id }]
    : [];

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Signature Requirement Rule</DialogTitle>
      <DialogContent className={classes.modalContent}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item container xs={12} spacing={2}>
              {/* Name Field */}
              <Grid item xs={8}>
                <Field
                  name="name"
                  component={renderTextField}
                  label="Name *"
                  validate={required}
                  width="100%"
                />
              </Grid>

              {/* Site Field (Disabled) */}
              <Grid item xs={4}>
                <Field
                  name="site"
                  component={renderDropdown}
                  label="Site *"
                  fields={siteOptions}
                  disabled
                  width="100%"
                />
              </Grid>
            </Grid>

            {/* Trigger Field */}
            <Grid item xs={4}>
              <Field
                name="trigger"
                component={renderDropdown}
                label="Trigger *"
                fields={triggerOptions}
                validate={required}
                width="100%"
              />
            </Grid>

            {/* Conditional Insurance Fields */}
            {showInsuranceFields && (
              <>
                <Grid item xs={2}>
                  <Field
                    name="insurance_bin"
                    component={renderTextField}
                    label="BIN *"
                    validate={required}
                    width="100%"
                  />
                </Grid>

                <Grid item xs={2}>
                  <Field
                    name="insurance_pcn"
                    component={renderTextField}
                    label="PCN"
                    width="100%"
                  />
                </Grid>

                <Grid item xs={2}>
                  <Field
                    name="insurance_group"
                    component={renderTextField}
                    label="Group"
                    width="100%"
                  />
                </Grid>
              </>
            )}

            {/* Buttons */}
            <Grid item xs={12}>
              <div className={classes.buttonContainer}>
                <Button onClick={handleClose} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AddSignatureRequirementModalWithStyles = withStyles(AddSignatureRequirementModalStyles)(
  AddSignatureRequirementModalComponent,
);

export const AddSignatureRequirementModal = reduxForm<IFormValues, IAddModalProps>({
  form: 'AddSignatureRequirementModal',
  enableReinitialize: true,
})(AddSignatureRequirementModalWithStyles as any);
